import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { ChevronRight, ArrowLeft, ChevronDown, Check, X, ArrowUpDown, ArrowUp, ArrowDown, Calendar, Info, Plus, Minus, Eye } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MetricTooltip } from '../components/MetricTooltip';
import AvailabilityPill from '../components/AvailabilityPill';
import { classifyRowsByKey, keepDateWiseRows, buildChartCaption } from '../lib/availability';
import { useMetadataIcons } from '../lib/useMetadataIcons';
import MonthCompare, { colorForMonth, dashForMonth, formatMonthChip } from '../components/MonthCompare';
import { toast } from 'sonner';
import { AD_PREVIEWS, getAdPreviewKey } from '../lib/adPreviewsSpec';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = ['Flipkart', 'Amazon', 'Zepto', 'Blinkit', 'D2C'];
const STAGES_OPTIONS = ['awareness', 'conversion'];
const BRANDS = ['Wellcore', 'YouWeFit', 'Dynamite', 'Osmo', 'Okami', 'Zero Sugar'];

// Per-channel drill-down hierarchy: { channel: { secondLayer: [ad_products] } }.
// For Flipkart/Amazon/Zepto/Blinkit the second layer is "On Platform Ads /
// Off Platform Ads". For D2C the user requested skipping the platform-type
// layer entirely and drilling directly by "Ad Channel" (Meta Ads / Google
// Ads / WhatsApp), so its second-layer keys ARE the ad-channel groupings.
// The drill-table reads the keys of AD_FORMATS[drillChannel] dynamically, so
// any channel added here just works — no further wiring needed.
const AD_FORMATS = {
  Flipkart: { 'On Platform Ads': ['PLA', 'PCA', 'Brand Ads'], 'Off Platform Ads': ['Telegram Ads', 'Whatsapp Ads', 'Google Ads', 'Meta Ads', 'Meta Ads by FK', 'Criteo by FK'] },
  Amazon:   { 'On Platform Ads': ['Sponsored Products', 'Sponsored Brand', 'Sponsored Display', 'ONO DSP'], 'Off Platform Ads': ['3P DSP', 'Telegram Ads', 'Whatsapp Ads', 'Google Ads', 'Meta Ads'] },
  Zepto:    { 'On Platform Ads': ['Sponsored Products', 'Sponsored Brand', 'Sponsored Display'] },
  // Blinkit mirrors Zepto's marketplace SP/SB/SD structure. Ad Properties
  // tables for Blinkit are still WIP — when those uploads land, the rows
  // are auto-picked up here because the channel name matches the DB field.
  Blinkit:  { 'On Platform Ads': ['Sponsored Products', 'Sponsored Brand', 'Sponsored Display'] },
  D2C:      { 'Meta Ads': ['Instagram', 'Facebook', 'Threads'], 'Google Ads': ['YouTube', 'Search Network'], 'WhatsApp': ['WhatsApp'] },
};

// Classifier for the SECOND drill layer. For marketplaces this returns the
// "On Platform Ads" / "Off Platform Ads" bucket. For D2C it returns the
// Ad Channel (Meta Ads / Google Ads / WhatsApp) so the drill mirrors the
// Ad Properties D2C hierarchy.
const getPlatformType = (row) => {
  const prod = row.ad_product || '';
  const ch = row.channel || '';
  if (ch === 'Flipkart') return ['PLA', 'PCA', 'Brand Ads'].includes(prod) ? 'On Platform Ads' : 'Off Platform Ads';
  if (ch === 'Amazon')   return ['Sponsored Products', 'Sponsored Brand', 'Sponsored Display', 'ONO DSP'].includes(prod) ? 'On Platform Ads' : 'Off Platform Ads';
  if (ch === 'Zepto' || ch === 'Blinkit') return 'On Platform Ads';
  if (ch === 'D2C') {
    if (['Instagram', 'Facebook', 'Threads'].includes(prod)) return 'Meta Ads';
    if (['YouTube', 'Search Network'].includes(prod))        return 'Google Ads';
    if (prod === 'WhatsApp')                                 return 'WhatsApp';
    return 'Other';
  }
  return 'Other';
};

export default function FullFunnel({ user }) {
  const navigate = useNavigate();
  const [headerFilters, setHeaderFilters] = useState({});
  const { brandIcon, platformIcon } = useMetadataIcons();
  const [mappings, setMappings] = useState([]);
  const [adData, setAdData] = useState([]);
  const [prevAdData, setPrevAdData] = useState([]); // Previous period data
  const [prevPeriodLabel, setPrevPeriodLabel] = useState('');
  const [loading, setLoading] = useState(true);

  // Multi-select filter state (outside Header)
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showChDD, setShowChDD] = useState(false);
  const [showStDD, setShowStDD] = useState(false);
  const [showBrDD, setShowBrDD] = useState(false);

  // Drill-down
  const [drillStage, setDrillStage] = useState(null);
  const [drillChannel, setDrillChannel] = useState(null);
  const [drillPlatform, setDrillPlatform] = useState(null);
  const [drillFormat, setDrillFormat] = useState(null);  // Level 5: campaign view
  const [drillDate, setDrillDate] = useState(null);
  const [previewProduct, setPreviewProduct] = useState(null);  // AD_PREVIEWS key for the placement modal

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, dir: 'desc' });
  const [campaignSort, setCampaignSort] = useState({ key: 'ad_spend', dir: 'desc' });

  // Metric cards — per-level so each drill state remembers its own picks.
  // Stored in localStorage under 'trace_funnel_metrics_by_level'.
  const [metricsByLevel, setMetricsByLevel] = useState(() => {
    try {
      const raw = localStorage.getItem('trace_funnel_metrics_by_level');
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  });
  useEffect(() => {
    try { localStorage.setItem('trace_funnel_metrics_by_level', JSON.stringify(metricsByLevel)); } catch { /* ignore */ }
  }, [metricsByLevel]);
  const [showMetricPicker, setShowMetricPicker] = useState(false);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customFormula, setCustomFormula] = useState('');
  // Hide-graphs toggle — persisted per page so it survives reloads.
  const [hideGraphs, setHideGraphs] = useState(() => {
    try { return localStorage.getItem('trace_funnel_hide_graphs') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('trace_funnel_hide_graphs', hideGraphs ? '1' : '0'); } catch { /* ignore */ }
  }, [hideGraphs]);

  // Monthly comparison state
  const [comparedMonths, setComparedMonths] = useState([]);
  const [comparisonData, setComparisonData] = useState({}); // {monthKey: rows[]}
  const [availableMonths, setAvailableMonths] = useState([]);

  const startDate = headerFilters.start_date || '';
  const endDate = headerFilters.end_date || '';
  const dateMode = headerFilters.dateMode || 'daily';
  const thirdGraph = headerFilters.thirdGraph || 'acos';
  // In Monthly view, currentMonth is driven by the date range we set for that month.
  // In Daily view, it's informational (used only if user later switches to Monthly).
  const currentMonth = (endDate || new Date().toISOString().slice(0, 10)).substring(0, 7);

  // Helper — given YYYY-MM, return the (startDate, endDate) that span that month.
  const monthBounds = (ym) => {
    const [y, mm] = ym.split('-');
    const yNum = parseInt(y, 10);
    const mNum = parseInt(mm, 10);
    const last = new Date(yNum, mNum, 0).getDate();
    return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, '0')}` };
  };

  // Set both the full-page date range (drives the metric fetch) AND reset the
  // comparison chip strip to only the picked month.
  const setBaseMonth = (ym) => {
    if (!ym || ym.length < 7) return;
    const { start, end } = monthBounds(ym);
    setComparedMonths([ym]);
    setHeaderFilters(prev => ({ ...prev, start_date: start, end_date: end }));
  };

  // When user switches Daily → Monthly, snap the date range to today's calendar month
  // so the "(current)" chip reads today's month dynamically and data reflects it.
  useEffect(() => {
    if (dateMode !== 'monthly') return;
    const today = new Date();
    const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    if (currentMonth !== thisMonth) {
      setBaseMonth(thisMonth);
    }
    // eslint-disable-next-line
  }, [dateMode]);

  useEffect(() => {
    fetchMappings();
  }, []);

  useEffect(() => {
    if (startDate && endDate) fetchAdData();
  }, [startDate, endDate]); // eslint-disable-line

  // On first mount only: if the user hasn't explicitly picked a date range yet,
  // default to the LATEST month with data. Header's last-30-days default may fall
  // outside the uploaded data window, so we correct it once here.
  // We use a ref to ensure this runs exactly once and never overrides a user pick.
  const didInitDateRef = useRef(false);
  const isRedirectingRef = useRef(false);

  // Shared 401 handler — ensures only one toast + exactly one navigate even if
  // multiple in-flight requests fail simultaneously.
  const handle401 = () => {
    if (isRedirectingRef.current) return;
    isRedirectingRef.current = true;
    toast.error('Session expired — please sign in again.', { id: 'session-expired' });
    navigate('/login');
  };
  useEffect(() => {
    if (didInitDateRef.current) return;
    // Default date range = the previous CALENDAR month (e.g. today 2026-02-13
    // → 2026-01-01 to 2026-01-31). Ignores whether any data exists in that
    // month; user can widen the picker manually if needed.
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    const toISO = (d) => d.toISOString().split('T')[0];
    didInitDateRef.current = true;
    setHeaderFilters(prev => ({ ...prev, start_date: toISO(start), end_date: toISO(end) }));
  }, []);

  // Available months list (Funnel Split spans across channels — fetch all)
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API}/ad-properties/available-months`, { credentials: 'include' });
        if (res.status === 401) {
          handle401();
          return;
        }
        if (res.ok) {
          const { months } = await res.json();
          if (!cancel) setAvailableMonths(months || []);
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, []);

  // Reset comparison whenever current month changes
  useEffect(() => {
    setComparedMonths(currentMonth ? [currentMonth] : []);
    setComparisonData({});
  }, [currentMonth]);

  // Fetch any compared month's data we don't have cached
  useEffect(() => {
    if (dateMode !== 'monthly') return;
    const missing = comparedMonths.filter(m => !comparisonData[m]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const updates = {};
      for (const m of missing) {
        try {
          const [y, mm] = m.split('-');
          const last = new Date(parseInt(y, 10), parseInt(mm, 10), 0).getDate();
          // Funnel Split spans every connected channel — fetch in parallel.
          const fetches = CHANNELS.map(ch => {
            const params = new URLSearchParams({ channel: ch });
            params.set('date_start', `${m}-01`);
            params.set('date_end', `${m}-${String(last).padStart(2, '0')}`);
            return fetch(`${API}/ad-properties/data?${params}&exclude_extra=1`, { credentials: 'include' }).then(r => {
              if (r.status === 401) {
                handle401();
                return { rows: [] };
              }
              if (!r.ok) {
                toast.error(`Failed to load ${ch} comparison data (HTTP ${r.status}).`);
                return { rows: [] };
              }
              return r.json();
            });
          });
          const results = await Promise.all(fetches);
          updates[m] = results.flatMap(r => r.rows || []);
        } catch (e) { updates[m] = []; }
      }
      if (!cancelled) setComparisonData(prev => ({ ...prev, ...updates }));
    })();
    return () => { cancelled = true; };
  }, [comparedMonths, dateMode]); // eslint-disable-line

  const addCompareMonth = (m) => {
    if (comparedMonths.length >= 6) return;
    if (comparedMonths.includes(m)) return;
    setComparedMonths([...comparedMonths, m]);
  };
  const removeCompareMonth = (m) => {
    if (m === currentMonth) return;
    setComparedMonths(comparedMonths.filter(x => x !== m));
  };

  // Centralised response handler: redirects to /login on 401, toasts on
  // other failures, and returns the parsed JSON on success. Each caller
  // supplies a `fallback` shape that's used when redirect / failure happens.
  const handleApi = async (res, fallback, label = 'data') => {
    if (res.status === 401) {
      handle401();
      return fallback;
    }
    if (!res.ok) {
      console.error(`[FullFunnel] ${label} failed:`, res.status, res.statusText);
      toast.error(`Failed to load ${label} (HTTP ${res.status}).`);
      return fallback;
    }
    return res.json();
  };

  const fetchMappings = async () => {
    try {
      const res = await fetch(`${API}/funnel-mappings`, { credentials: 'include' });
      const data = await handleApi(res, null, 'funnel mappings');
      if (data) setMappings(data);
    } catch (e) { console.error(e); }
  };

  const fetchAdData = async () => {
    setLoading(true);
    try {
      const buildParams = (channel, startD, endD) => {
        const p = new URLSearchParams({ channel });
        if (startD) p.set('date_start', startD);
        if (endD) p.set('date_end', endD);
        return p;
      };

      // Calculate previous comparison period
      let prevStartStr = null, prevEndStr = null;
      if (startDate && endDate) {
        const s = new Date(startDate);
        const e = new Date(endDate);
        const days = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
        const prevEnd = new Date(s);
        prevEnd.setDate(prevEnd.getDate() - 1);
        const prevStart = new Date(prevEnd);
        prevStart.setDate(prevStart.getDate() - days + 1);
        const fmt = (d) => d.toISOString().split('T')[0];
        prevStartStr = fmt(prevStart);
        prevEndStr = fmt(prevEnd);
        setPrevPeriodLabel(`vs prev ${days} day${days > 1 ? 's' : ''}`);
      } else {
        setPrevPeriodLabel('');
      }

      // Funnel Split aggregates across all channels — fetch each in parallel
      const channels = CHANNELS;
      const fetchOne = async (ch, sd, ed, label) => {
        const res = await fetch(`${API}/ad-properties/data?${buildParams(ch, sd, ed)}&exclude_extra=1`, { credentials: 'include' });
        return handleApi(res, { rows: [] }, label);
      };
      const currentReqs = channels.map(ch => fetchOne(ch, startDate, endDate, `${ch} ad data`));
      const prevReqs = (prevStartStr && prevEndStr)
        ? channels.map(ch => fetchOne(ch, prevStartStr, prevEndStr, `${ch} comparison data`))
        : null;

      const [currResults, prevResults] = await Promise.all([
        Promise.all(currentReqs),
        prevReqs ? Promise.all(prevReqs) : Promise.resolve(null),
      ]);

      setAdData(currResults.flatMap(d => d.rows || []));
      setPrevAdData(prevResults ? prevResults.flatMap(d => d.rows || []) : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // Build mapping lookup with stage + 4 splits per campaign
  const mappingLookup = {};
  for (const m of mappings) {
    mappingLookup[m.campaign_name] = {
      stage: m.funnel_stage,
      sa: m.spends_awareness_pct ?? 0,
      sc: m.spends_conversion_pct ?? 0,
      ra: m.sales_awareness_pct ?? 0,
      rc: m.sales_conversion_pct ?? 0,
    };
  }

  // Enrich each row with its mapping splits. Unmapped rows have stage='unmapped' and 0 splits.
  const enriched = adData.map(r => {
    const lk = mappingLookup[r.campaign_name];
    if (!lk) return { ...r, funnel_stage: 'unmapped', _sa: 0, _sc: 0, _ra: 0, _rc: 0 };
    return { ...r, funnel_stage: lk.stage, _sa: lk.sa, _sc: lk.sc, _ra: lk.ra, _rc: lk.rc };
  });

  // Detect ad-products with daily-level data (at least one month has >1 distinct date)
  const productHasDailyData = (() => {
    const byProd = {};
    for (const r of enriched) {
      if (!r.ad_product || !r.date) continue;
      const m = String(r.date).substring(0, 7);
      const k = r.ad_product;
      byProd[k] = byProd[k] || {};
      (byProd[k][m] = byProd[k][m] || new Set()).add(r.date);
    }
    const result = {};
    for (const [prod, months] of Object.entries(byProd)) {
      result[prod] = Object.values(months).some(s => s.size > 1);
    }
    return result;
  })();

  // ── Availability classification (shared with backend & Ad Format Breakdown) ──
  // Classify each row-group at the level currently on screen so we can:
  //   1) render a pill next to each row's name
  //   2) exclude non-date-wise rows from the line chart
  //   3) build a dynamic caption listing what's in / out of the chart
  const classifyForLevel = (rows, level) => {
    // level is one of "stage" | "channel" | "platform" | "format" | "campaign"
    const keyFn = (r) => {
      if (level === 'stage')    return r.funnel_stage || 'unmapped';
      if (level === 'channel')  return r.channel || '';
      if (level === 'platform') return getPlatformType(r);
      if (level === 'format')   return r.ad_product || '';
      if (level === 'campaign') return r.campaign_name || '';
      return '';
    };
    return { keyFn, byKey: classifyRowsByKey(rows, keyFn) };
  };

  const baseFiltered = enriched.filter(r => {
    if (selectedChannels.length > 0 && !selectedChannels.includes(r.channel)) return false;
    if (selectedStages.length > 0 && !selectedStages.includes(r.funnel_stage)) return false;
    if (selectedBrands.length > 0 && !selectedBrands.includes(r.brand)) return false;
    // CHANGE 2: always include rows for monthly-only properties — the UI surfaces a banner instead of hiding.
    return true;
  });
  // Apply date drill
  const filtered = drillDate ? baseFiltered.filter(r => r.date === drillDate) : baseFiltered;

  // Helper: which weight pair to use for a target stage. Spend weight applies to spend/views/clicks (media volume),
  // sales weight applies to revenue/units (conversion outcome).
  const stageWeights = (row, stageKey) => {
    if (stageKey === 'awareness') return { sw: (row._sa || 0) / 100, rw: (row._ra || 0) / 100 };
    if (stageKey === 'conversion') return { sw: (row._sc || 0) / 100, rw: (row._rc || 0) / 100 };
    return { sw: 1, rw: 1 }; // 'unmapped' bucket — full row contribution
  };

  // ── Aggregation ──
  // When stageKey is provided, each row contributes weighted by its mapping splits for that stage.
  // When omitted (e.g., totals view), the full row counts at 100%.
  const aggregate = (rows, stageKey = null) => {
    let spend = 0, revenue = 0, views = 0, clicks = 0, units = 0;
    for (const r of rows) {
      let sw = 1, rw = 1;
      if (stageKey) ({ sw, rw } = stageWeights(r, stageKey));
      spend += (r.ad_spend || 0) * sw;
      views += (r.views || 0) * sw;
      clicks += (r.clicks || 0) * sw;
      revenue += (r.total_revenue || 0) * rw;
      units += (r.total_converted_units || 0) * rw;
    }
    const gstSpend = spend * 1.18;
    return { ad_spend: spend, total_revenue: revenue, views, clicks, total_converted_units: units,
      impressions: views, // schema treats "views" as impressions for marketplaces; alias for UX clarity
      cpv: views > 0 ? gstSpend / views : 0,
      cpm: views > 0 ? (gstSpend / views) * 1000 : 0,
      roi: gstSpend > 0 ? revenue / gstSpend : 0, acos: revenue > 0 ? (gstSpend / revenue) * 100 : 0,
      tacos: revenue > 0 ? (gstSpend / (revenue * 1.15)) * 100 : 0,
      ctr: views > 0 ? (clicks / views) * 100 : 0, cvr: clicks > 0 ? (units / clicks) * 100 : 0,
      count: rows.length };
  };

  // Rows that are eligible to contribute to a given stage (= mapped + non-zero split for that stage)
  const rowsContributingTo = (rows, stageKey) => {
    if (!stageKey || stageKey === 'unmapped') return rows.filter(r => r.funnel_stage === 'unmapped');
    return rows.filter(r => {
      if (r.funnel_stage === 'unmapped') return false;
      if (stageKey === 'awareness') return (r._sa || 0) > 0;
      if (stageKey === 'conversion') return (r._sc || 0) > 0;
      return false;
    });
  };

  // ── Chart data — DATE-WISE ONLY. Non-date-wise rows are excluded entirely ──
  // (not distributed, not interpolated). Their totals still appear in the table
  // and in the summary cards; they simply don't contribute to the cumulative line.
  const _daysInMonth = (yyyymm) => {
    const [y, m] = yyyymm.split('-').map(Number);
    return new Date(y, m, 0).getDate();
  };

  // Decide the CURRENT drill level for the graph — matches the table that's
  // rendered right below the chart, so pill decisions and chart decisions stay
  // in sync.
  const currentLevel = drillFormat ? 'campaign'
                     : drillPlatform ? 'format'
                     : drillChannel ? 'platform'
                     : drillStage ? 'channel'
                     : 'stage';

  // Rows that feed the CURRENT drill view (respecting all filters + drill state).
  const chartScopeRows = (() => {
    const base = drillStage ? rowsContributingTo(filtered, drillStage) : filtered;
    if (drillFormat)   return base.filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform && r.ad_product === drillFormat);
    if (drillPlatform) return base.filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform);
    if (drillChannel)  return base.filter(r => r.channel === drillChannel);
    return base;
  })();

  // Classify at the level currently on screen (for pill display) and also
  // classify by ad_product (the true data-source unit) so the chart can
  // always exclude non-date-wise rows regardless of the current drill level.
  const classificationEnabled = currentLevel !== 'stage';
  const { byKey: chartClassByKeyRaw } = classifyForLevel(chartScopeRows, currentLevel);
  const chartClassByKey = classificationEnabled ? chartClassByKeyRaw : {};

  // Always filter chart series by ad_product-level availability. This ensures
  // month-wise / week-wise properties are excluded from the cumulative line
  // even at the top-most (stage) view.
  const chartAdProductClass = classifyRowsByKey(chartScopeRows, r => r.ad_product || '');
  const chartRowsDateWise = keepDateWiseRows(chartScopeRows, r => r.ad_product || '', chartAdProductClass);

  const getLineChartData = () => {
    const rows = chartRowsDateWise;
    if (!rows.length) return [];
    const bucket = {};
    for (const r of rows) {
      if (!r.date) continue;
      const sw = drillStage ? stageWeights(r, drillStage).sw : 1;
      const rw = drillStage ? stageWeights(r, drillStage).rw : 1;
      if (!bucket[r.date]) bucket[r.date] = { spend: 0, revenue: 0 };
      bucket[r.date].spend   += (r.ad_spend || 0) * sw;
      bucket[r.date].revenue += (r.total_revenue || 0) * rw;
    }
    const inWindow = (d) => !startDate || !endDate || (d >= startDate && d <= endDate);
    const sorted = Object.keys(bucket).filter(inWindow).sort();

    if (dateMode === 'monthly') {
      const byMonth = {};
      for (const d of sorted) {
        const m = d.substring(0, 7);
        if (!byMonth[m]) byMonth[m] = { spend: 0, revenue: 0 };
        byMonth[m].spend   += bucket[d].spend;
        byMonth[m].revenue += bucket[d].revenue;
      }
      return Object.entries(byMonth).map(([m, v]) => {
        const gs = v.spend * 1.18;
        return {
          date: new Date(m + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          rawDate: m,
          spends: Math.round(v.spend), sales: Math.round(v.revenue),
          acos: v.revenue > 0 ? +((gs / v.revenue) * 100).toFixed(1) : 0,
          tacos: v.revenue > 0 ? +((gs / (v.revenue * 1.15)) * 100).toFixed(1) : 0,
          roas: gs > 0 ? +(v.revenue / gs).toFixed(2) : 0,
          estimated: false,
        };
      });
    }

    return sorted.map((d) => {
      const v = bucket[d];
      const gs = v.spend * 1.18;
      return {
        date: new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        rawDate: d,
        spends: Math.round(v.spend), sales: Math.round(v.revenue),
        acos: v.revenue > 0 ? +((gs / v.revenue) * 100).toFixed(1) : 0,
        tacos: v.revenue > 0 ? +((gs / (v.revenue * 1.15)) * 100).toFixed(1) : 0,
        roas: gs > 0 ? +(v.revenue / gs).toFixed(2) : 0,
        estimated: false,
      };
    });
  };
  const lineData = getLineChartData();
  const isWholeLineEstimated = false; // exclusion-based now — no estimation

  // Build dynamic caption + empty-state title from the ad_product-level
  // classification map — always populated so the note appears at every level.
  const chartEntries = Object.entries(chartAdProductClass)
    .filter(([n]) => n)
    .map(([name, cls]) => ({ name, type: cls.availability_type }));
  const chartCaption = buildChartCaption(chartEntries);
  const hasAnyDateWise = chartEntries.some(e => e.type === 'date-wise' || e.type === 'mixed');

  // Apply same downstream filters (channels/stages/brands/drillStage) to comparison-month rows
  const applyFilters = (rs) => {
    return rs.filter(r => {
      // Mapping: derive funnel_stage like enriched does
      const map = mappings.find(mp => mp.campaign_name === r.campaign_name);
      const fs = map ? map.funnel_stage : 'unmapped';
      if (selectedChannels.length > 0 && !selectedChannels.includes(r.channel)) return false;
      if (selectedStages.length > 0 && !selectedStages.includes(fs)) return false;
      if (selectedBrands.length > 0 && !selectedBrands.includes(r.brand)) return false;
      if (drillStage && fs !== drillStage) return false;
      return true;
    });
  };

  const buildMonthCompareData = () => {
    if (dateMode !== 'monthly') return [];
    const sortedMonths = [...comparedMonths].sort();
    return sortedMonths.map(m => {
      const scope = applyFilters(comparisonData[m] || []);
      let spend = 0, revenue = 0, views = 0, clicks = 0, units = 0;
      for (const r of scope) {
        const lk = mappingLookup[r.campaign_name];
        let sw = 1, rw = 1;
        if (drillStage) {
          if (!lk) { sw = 0; rw = 0; }
          else if (drillStage === 'awareness') { sw = (lk.sa || 0) / 100; rw = (lk.ra || 0) / 100; }
          else if (drillStage === 'conversion') { sw = (lk.sc || 0) / 100; rw = (lk.rc || 0) / 100; }
        }
        spend += (r.ad_spend || 0) * sw;
        views += (r.views || 0) * sw;
        clicks += (r.clicks || 0) * sw;
        revenue += (r.total_revenue || 0) * rw;
        units += (r.total_converted_units || 0) * rw;
      }
      const gst = spend * 1.18;
      return {
        monthKey: m,
        month: formatMonthChip(m),
        spends: Math.round(spend),
        sales: Math.round(revenue),
        ctr: views > 0 ? +(clicks / views * 100).toFixed(2) : 0,
        cvr: clicks > 0 ? +(units / clicks * 100).toFixed(2) : 0,
        acos: revenue > 0 ? +((gst / revenue) * 100).toFixed(2) : 0,
        tacos: revenue > 0 ? +((gst / (revenue * 1.15)) * 100).toFixed(2) : 0,
        roas: gst > 0 ? +(revenue / gst).toFixed(2) : 0,
      };
    });
  };
  const compareData = buildMonthCompareData();
  const isCompareMode = dateMode === 'monthly' && comparedMonths.length > 0 && compareData.length > 0;

  // Custom dot for monthly comparison: colored by chip color
  const MonthCompareDot = (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null) return null;
    const color = colorForMonth(payload.monthKey, comparedMonths);
    return <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />;
  };

  const fmtChart = v => v >= 1e7 ? `${(v / 1e7).toFixed(2)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(1)}L` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : `${Math.round(v)}`;
  const fmtVal = (val, fmt) => {
    if (val === undefined || val === null) return '-';
    if (fmt === 'currency') return `Rs. ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    if (fmt === 'number') return Number(val).toLocaleString('en-IN');
    if (fmt === 'pct') return `${Number(val).toFixed(2)}%`;
    if (fmt === 'decimal') return Number(val).toFixed(2);
    return val;
  };

  const graphs = [
    { key: 'spends', label: 'Spends', color: '#32B4AC', format: fmtChart },
    { key: 'sales', label: 'Revenue', color: '#6366f1', format: fmtChart },
  ];
  const thirdCfg = { acos: { key: 'acos', label: 'ACOS', color: '#f59e0b', format: v => `${v}%` }, tacos: { key: 'tacos', label: 'TACOS', color: '#ef4444', format: v => `${v}%` }, roas: { key: 'roas', label: 'ROI', color: '#10b981', format: v => `${v}x` } };
  const tg = thirdCfg[thirdGraph];

  const ClickableDot = (props) => {
    const { cx, cy, payload, fill } = props;
    return <circle cx={cx} cy={cy} r={6} fill={fill || '#32B4AC'} stroke="#fff" strokeWidth={2} style={{ cursor: dateMode === 'daily' ? 'pointer' : 'default' }} onClick={(e) => { e.stopPropagation(); if (dateMode === 'daily' && payload?.rawDate) setDrillDate(payload.rawDate); }} />;
  };

  // ── Sorting (3-state cycle: asc → desc → unsorted) ──
  const toggleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: null }; // 3rd click resets
    });
  };
  const SortIcon = ({ colKey }) => {
    if (sortConfig.key !== colKey) return <ArrowUpDown className="w-3 h-3 text-gray-300 ml-0.5 inline opacity-60 group-hover:opacity-100" />;
    return sortConfig.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-[#32B4AC] ml-0.5 inline" />
      : <ArrowDown className="w-3 h-3 text-[#32B4AC] ml-0.5 inline" />;
  };
  // Compare two values: numeric when both parse as numbers, else case-insensitive string.
  const _cmp = (av, bv) => {
    const an = typeof av === 'number' ? av : parseFloat(av);
    const bn = typeof bv === 'number' ? bv : parseFloat(bv);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    return String(av ?? '').toLowerCase().localeCompare(String(bv ?? '').toLowerCase());
  };
  const applySort = (rows, key) => {
    if (!sortConfig.key) return rows;
    const k = key || sortConfig.key;
    const sorted = [...rows].sort((a, b) => _cmp(a[k], b[k]));
    return sortConfig.dir === 'desc' ? sorted.reverse() : sorted;
  };

  // ── Metric cards ──
  // 'ad_spend' card displays the GST-inclusive value (spend × 1.18) — a
  // dedicated key `ad_spend_gst` is derived inside the card render below so
  // the underlying `ad_spend` sum stays available for other charts / prev-vs
  // deltas. ACOS sits next to CVR per the funnel-order request.
  const ALL_METRICS = [
    { key: 'total_revenue', label: 'Revenue', fmt: 'currency' },
    { key: 'ad_spend', label: 'Spends (Incl. GST)', fmt: 'currency', gstInclusive: true },
    { key: 'total_converted_units', label: 'Purchases', fmt: 'number' },
    { key: 'roi', label: 'RoAS', fmt: 'decimal' },
    { key: 'clicks', label: 'Clicks', fmt: 'number' },
    { key: 'cvr', label: 'Conversion Rate', fmt: 'pct' },
    { key: 'impressions', label: 'Impressions', fmt: 'number' },
    { key: 'views', label: 'Views', fmt: 'number' },
    { key: 'ctr', label: 'CTR', fmt: 'pct' },
    { key: 'cpv', label: 'CPV', fmt: 'currency' },
    { key: 'cpm', label: 'CPM', fmt: 'currency' },
    { key: 'acos', label: 'ACOS', fmt: 'pct' },
  ];

  // Default scorecards per funnel stage. Any user override is stored per-level
  // in localStorage so switching drill state keeps that user's picks for each
  // stage independent.
  const DEFAULT_METRICS_BY_LEVEL = {
    overview:      ['total_revenue', 'ad_spend', 'total_converted_units', 'roi', 'clicks', 'cvr'],
    awareness:     ['impressions', 'views', 'clicks', 'ctr', 'cpv', 'cpm'],
    conversion:    ['total_revenue', 'ad_spend', 'total_converted_units', 'roi'],
    consideration: ['total_revenue', 'ad_spend', 'clicks', 'ctr', 'cvr', 'roi'],
    unmapped:      ['total_revenue', 'ad_spend', 'total_converted_units', 'roi', 'clicks', 'cvr'],
  };
  const metricsLevelKey = drillStage || 'overview';
  // Resolve visible metrics for the current level. Falls back to defaults if
  // the user hasn't customised for this level yet.
  const visibleMetrics = (metricsByLevel[metricsLevelKey] && metricsByLevel[metricsLevelKey].length > 0)
    ? metricsByLevel[metricsLevelKey]
    : (DEFAULT_METRICS_BY_LEVEL[metricsLevelKey] || DEFAULT_METRICS_BY_LEVEL.overview);
  const setVisibleMetrics = (updater) => {
    setMetricsByLevel(prev => {
      const cur = prev[metricsLevelKey] ?? visibleMetrics;
      const next = typeof updater === 'function' ? updater(cur) : updater;
      return { ...prev, [metricsLevelKey]: next };
    });
  };
  const fmtMetric = (val, fmt) => {
    if (val === undefined || val === null) return '-';
    if (fmt === 'currency') { if (val >= 1e7) return `Rs. ${(val / 1e7).toFixed(2)}Cr`; if (val >= 1e5) return `Rs. ${(val / 1e5).toFixed(2)}L`; if (val >= 1e3) return `Rs. ${(val / 1e3).toFixed(1)}K`; return `Rs. ${Math.round(val).toLocaleString('en-IN')}`; }
    if (fmt === 'number') { if (val >= 1e7) return `${(val / 1e7).toFixed(2)}Cr`; if (val >= 1e5) return `${(val / 1e5).toFixed(2)}L`; if (val >= 1e3) return `${(val / 1e3).toFixed(2)}K`; return Math.round(val).toLocaleString('en-IN'); }
    if (fmt === 'pct') return `${val.toFixed(2)}%`; if (fmt === 'decimal') return val.toFixed(2); return val;
  };

  // Current context rows for metrics — deeper drill = more specific. Each row contributes weighted by drillStage split.
  const contextStageKey = drillStage || null;
  const contextRows = drillFormat
    ? rowsContributingTo(filtered, drillStage).filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform && r.ad_product === drillFormat)
    : drillPlatform
    ? rowsContributingTo(filtered, drillStage).filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform)
    : drillChannel
    ? rowsContributingTo(filtered, drillStage).filter(r => r.channel === drillChannel)
    : drillStage
    ? rowsContributingTo(filtered, drillStage)
    : filtered;
  const currentMetrics = aggregate(contextRows, contextStageKey);

  // Compare against previous equivalent period — same enrichment + weighting
  const prevEnriched = prevAdData.map(r => {
    const lk = mappingLookup[r.campaign_name];
    if (!lk) return { ...r, funnel_stage: 'unmapped', _sa: 0, _sc: 0, _ra: 0, _rc: 0 };
    return { ...r, funnel_stage: lk.stage, _sa: lk.sa, _sc: lk.sc, _ra: lk.ra, _rc: lk.rc };
  });
  const prevBaseFiltered = prevEnriched.filter(r => {
    if (selectedChannels.length > 0 && !selectedChannels.includes(r.channel)) return false;
    if (selectedStages.length > 0 && !selectedStages.includes(r.funnel_stage)) return false;
    if (selectedBrands.length > 0 && !selectedBrands.includes(r.brand)) return false;
    return true;
  });
  const prevContextRows = prevAdData.length > 0 ? (
    drillFormat ? rowsContributingTo(prevBaseFiltered, drillStage).filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform && r.ad_product === drillFormat)
    : drillPlatform ? rowsContributingTo(prevBaseFiltered, drillStage).filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform)
    : drillChannel ? rowsContributingTo(prevBaseFiltered, drillStage).filter(r => r.channel === drillChannel)
    : drillStage ? rowsContributingTo(prevBaseFiltered, drillStage)
    : prevBaseFiltered
  ) : [];
  const prevMetrics = prevAdData.length > 0 ? aggregate(prevContextRows, contextStageKey) : null;

  // Evaluate custom metric formula
  const evalCustom = (formula, data) => {
    try {
      const vars = { spend: (data.ad_spend || 0) * 1.18, revenue: data.total_revenue || 0, views: data.views || 0, clicks: data.clicks || 0, units: data.total_converted_units || 0, ctr: data.ctr || 0, cvr: data.cvr || 0, roi: data.roi || 0, acos: data.acos || 0 };
      const fn = new Function(...Object.keys(vars), `return ${formula}`);
      const result = fn(...Object.values(vars));
      return isFinite(result) ? result : 0;
    } catch { return 0; }
  };

  const MetricCards = () => {
    const allMetrics = [...ALL_METRICS, ...customMetrics];
    // Preserve the ORDER declared in visibleMetrics (or the level's default)
    // rather than iterating over ALL_METRICS — so Awareness renders as
    // Impressions, Views, Clicks, CTR, CPV, CPM per spec.
    const metrics = visibleMetrics.map(k => allMetrics.find(m => m.key === k)).filter(Boolean);
    return (
      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="metric-cards">
        {metrics.map(m => {
          const rawVal = m.formula ? evalCustom(m.formula, currentMetrics) : (currentMetrics[m.key] ?? 0);
          const rawPv = prevMetrics ? (m.formula ? evalCustom(m.formula, prevMetrics) : prevMetrics[m.key]) : null;
          // Apply GST inclusion for the Spend card without mutating the
          // underlying aggregate — keeps the pre-GST number available to
          // other consumers (charts, tables that still show pre-GST).
          const val = m.gstInclusive ? rawVal * 1.18 : rawVal;
          const pv = m.gstInclusive && rawPv !== null ? rawPv * 1.18 : rawPv;
          const pct = pv && pv !== 0 ? ((val - pv) / Math.abs(pv)) * 100 : null;
          return (
            <div key={m.key} className="bg-white border border-gray-200/80 rounded-xl px-4 py-3 shadow-sm min-w-[160px] flex-shrink-0 hover:shadow-md hover:-translate-y-0.5 transition-all duration-150">
              <div className="flex items-center gap-1.5 mb-2"><MetricTooltip label={m.key} /><span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">{m.label}</span></div>
              <div className="text-xl font-bold text-[#0F0F0F] leading-tight tabular-nums tracking-tight">{fmtMetric(val, m.fmt)}</div>
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {pct !== null && (
                  <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[11px] font-semibold ${pct >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                    <span className="text-[10px] leading-none">{pct >= 0 ? '↑' : '↓'}</span>{Math.abs(pct).toFixed(2)}%
                  </span>
                )}
                {pv !== null ? (
                  <span className="text-[10.5px] text-gray-400 font-medium tabular-nums">Prev: {fmtMetric(pv, m.fmt)}</span>
                ) : prevPeriodLabel ? (
                  <span className="text-[10.5px] text-gray-400 font-medium italic">No prev data</span>
                ) : null}
              </div>
              {prevPeriodLabel && <div className="text-[10px] text-gray-400 mt-0.5">{prevPeriodLabel}</div>}
            </div>
          );
        })}
        <button onClick={() => setShowMetricPicker(true)} className="bg-white border-2 border-dashed border-gray-200 rounded-xl px-4 py-3 min-w-[120px] flex-shrink-0 flex flex-col items-center justify-center gap-1 text-gray-400 hover:text-gray-600 hover:border-gray-300" data-testid="add-remove-metrics-btn">
          <Plus className="w-5 h-5" /><span className="text-[10px] font-medium">Add/Remove<br />Metrics</span>
        </button>
      </div>
    );
  };

  // ── Level data builders — split-aware ──
  // For each stage, contribute every mapped row weighted by its split for that stage.
  const stageRows = STAGES_OPTIONS.map(s => ({
    name: s.charAt(0).toUpperCase() + s.slice(1), key: s,
    ...aggregate(rowsContributingTo(filtered, s), s)
  }));
  const channelRows = drillStage ? (() => {
    const rows = CHANNELS.map(ch => ({
      name: ch, key: ch,
      ...aggregate(rowsContributingTo(filtered, drillStage).filter(r => r.channel === ch), drillStage)
    }));
    // Expand D2C inline — show Meta Ads / Google Ads / WhatsApp sub-rows
    // directly under D2C without requiring the user to drill in.
    const expanded = [];
    for (const r of rows) {
      expanded.push(r);
      if (r.key === 'D2C') {
        const subKeys = Object.keys(AD_FORMATS['D2C'] || {});
        for (const sub of subKeys) {
          const subRows = rowsContributingTo(filtered, drillStage).filter(x => x.channel === 'D2C' && getPlatformType(x) === sub);
          expanded.push({
            name: sub,
            key: `D2C::${sub}`,
            isChild: true,
            parentKey: 'D2C',
            ...aggregate(subRows, drillStage),
          });
        }
      }
    }
    return expanded;
  })() : [];
  // Second drill layer: for marketplaces this is On / Off Platform Ads; for
  // D2C it's Meta Ads / Google Ads / WhatsApp. Reads dynamically off the
  // selected channel's AD_FORMATS keys so each channel can have its own
  // shape (e.g. Zepto / Blinkit show only 'On Platform Ads').
  const platformRows = (drillStage && drillChannel) ? Object.keys(AD_FORMATS[drillChannel] || {}).map(pt => ({
    name: pt, key: pt,
    ...aggregate(rowsContributingTo(filtered, drillStage).filter(r => r.channel === drillChannel && getPlatformType(r) === pt), drillStage)
  })) : [];
  const formatRows = (drillStage && drillChannel && drillPlatform) ? (AD_FORMATS[drillChannel]?.[drillPlatform] || []).map(fmt => ({
    name: fmt, key: fmt,
    ...aggregate(rowsContributingTo(filtered, drillStage).filter(r => r.channel === drillChannel && getPlatformType(r) === drillPlatform && r.ad_product === fmt), drillStage)
  })) : [];

  // ── Multi-select dropdown ──
  const MultiDD = ({ label, options, selected, onChange, show, setShow, testId }) => (
    <div className="relative">
      <button onClick={() => setShow(!show)} className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 h-9 bg-white hover:bg-gray-50 shadow-sm text-sm min-w-[130px]" data-testid={testId}>
        <span className="text-gray-700 truncate">{selected.length === 0 ? label : selected.length === 1 ? (selected[0].charAt(0).toUpperCase() + selected[0].slice(1)) : `${selected.length} ${label}`}</span>
        <ChevronDown className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
      </button>
      {show && (
        <div className="absolute top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-xl py-1 min-w-[160px]">
          {options.map(o => (
            <button key={o} onClick={() => onChange(prev => prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o])} className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left">
              <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${selected.includes(o) ? 'bg-[#32B4AC] border-[#32B4AC]' : 'border-gray-300'}`}>{selected.includes(o) && <Check className="w-3 h-3 text-white" />}</div>
              <span className="text-gray-700 capitalize">{o}</span>
            </button>
          ))}
          {selected.length > 0 && <button onClick={() => onChange([])} className="w-full px-3 py-2 text-xs text-gray-400 hover:text-gray-600 border-t border-gray-100 text-left">Clear all</button>}
        </div>
      )}
    </div>
  );

  // ── Table renderer (mirrors Ad Properties) ──
  const RenderTable = ({ title, rows, totalSpend, totalRev, onRowClick, level, channelForPreview, classByKey }) => {
    const sorted = applySort(rows);
    const showPreviewCol = level === 'format' && !!channelForPreview;
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100"><h3 className="text-sm font-semibold text-[#0F0F0F]">{title}</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ tableLayout: 'fixed' }} data-testid={`funnel-table-${level}`}>
            <colgroup>
              <col style={{ width: '200px' }} />{/* Name */}
              {showPreviewCol && <col style={{ width: '90px' }} />}{/* Preview */}
              <col style={{ width: '150px' }} />{/* Spends (Incl. GST) */}
              <col style={{ width: '140px' }} />{/* Revenue */}
              <col style={{ width: '80px' }} />{/* ROI */}
              <col style={{ width: '90px' }} />{/* ACOS */}
              <col style={{ width: '150px' }} />{/* % Spends Alloc. */}
              <col style={{ width: '150px' }} />{/* % Revenue Alloc. */}
              {onRowClick && <col style={{ width: '24px' }} />}
            </colgroup>
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/80">
                <th className="text-left py-2.5 px-4 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Name</th>
                {showPreviewCol && (
                  <th className="text-center py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap align-middle">Preview</th>
                )}
                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('ad_spend')}><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="spends" /><span>Spends (Incl. GST)</span><SortIcon colKey="ad_spend" /></span></th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('total_revenue')}><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="revenue" /><span>Revenue</span><SortIcon colKey="total_revenue" /></span></th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('roi')}><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="roi" /><span>ROI</span><SortIcon colKey="roi" /></span></th>
                <th className="text-right py-2.5 px-3 font-semibold text-gray-500 text-xs uppercase tracking-wider cursor-pointer hover:text-gray-700 select-none whitespace-nowrap align-middle" onClick={() => toggleSort('acos')}><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="acos" /><span>ACOS</span><SortIcon colKey="acos" /></span></th>
                <th className="text-right py-2.5 pl-4 pr-3 font-semibold text-gray-500 text-xs uppercase tracking-wider border-l-2 border-blue-200 bg-blue-50/60 whitespace-nowrap align-middle"><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="spends_alloc" /><span>% Spends alloc.</span></span></th>
                <th className="text-right py-2.5 pl-4 pr-3 font-semibold text-gray-500 text-xs uppercase tracking-wider bg-blue-50/60 whitespace-nowrap align-middle"><span className="inline-flex items-center justify-end gap-1.5"><MetricTooltip label="sales_alloc" /><span>% Revenue alloc.</span></span></th>
                {onRowClick && <th></th>}
              </tr>
            </thead>
            <tbody>
              {sorted.map(row => {
                const sa = totalSpend > 0 ? ((row.ad_spend / totalSpend) * 100) : 0;
                const ra = totalRev > 0 ? ((row.total_revenue / totalRev) * 100) : 0;
                const previewKey = showPreviewCol ? getAdPreviewKey(channelForPreview, row.name) : null;
                return (
                  <tr key={row.key} className={`border-b border-gray-100 transition-colors ${onRowClick ? 'cursor-pointer hover:bg-gray-50/60' : 'hover:bg-gray-50/40'} ${row.isChild ? 'bg-gray-50/40' : ''}`}
                    onClick={() => onRowClick?.(row.key, row)} data-testid={`funnel-row-${row.key.replace(/[\s:]+/g, '-').toLowerCase()}`}>
                    <td className={`py-2.5 px-4 font-medium text-[#0F0F0F] align-middle ${row.isChild ? 'pl-10' : ''}`} title={row.name}>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {row.isChild && <span className="text-gray-300 text-xs">└─</span>}
                        {level === 'stage' && (
                          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${row.key === 'awareness' ? 'bg-sky-400' : row.key === 'conversion' ? 'bg-emerald-400' : row.key === 'unmapped' ? 'bg-gray-400' : 'bg-violet-400'}`}></span>
                        )}
                        {(() => {
                          // Show icon lookup based on the row's meaning.
                          // Channel/platform rows use platform icons; brand isn't a
                          // level here so we skip that unless later added.
                          const url = level === 'channel' || level === 'platform' ? platformIcon(row.name) : '';
                          return url ? <img src={url} alt="" className="w-4 h-4 rounded object-contain shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : null;
                        })()}
                        <span className={`break-words ${row.isChild ? 'text-gray-600 text-[13px]' : ''}`}>{row.name}</span>
                        {classByKey?.[row.key]?.availability_type && (
                          <AvailabilityPill type={classByKey[row.key].availability_type} size="xs" />
                        )}
                        {onRowClick && <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                      </div>
                    </td>
                    {showPreviewCol && (
                      <td className="py-2.5 px-3 text-center align-middle">
                        {previewKey ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPreviewProduct(previewKey); }}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-[#32B4AC] hover:border-[#32B4AC] hover:bg-[#32B4AC]/5 transition-colors"
                            title={`View ${row.name} ad placements`}
                            data-testid={`funnel-preview-btn-${row.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    )}
                    <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(row.ad_spend * 1.18, 'currency')}</td>
                    <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtVal(row.total_revenue, 'currency')}</td>
                    <td className="text-right py-2.5 px-3 font-medium tabular-nums whitespace-nowrap"><span className={row.roi >= 1 ? 'text-green-600' : row.roi > 0 ? 'text-yellow-600' : 'text-red-500'}>{(row.roi || 0).toFixed(2)}x</span></td>
                    <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap">{(row.acos || 0).toFixed(2)}%</td>
                    <td className="text-right py-2.5 pl-4 pr-3 font-medium text-blue-700 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">{sa.toFixed(2)}%</td>
                    <td className="text-right py-2.5 pl-4 pr-3 font-medium text-blue-700 tabular-nums bg-blue-50/30 whitespace-nowrap">{ra.toFixed(2)}%</td>
                    {onRowClick && <td className="py-2.5 pr-2"><ChevronRight className="w-3.5 h-3.5 text-gray-300" /></td>}
                  </tr>
                );
              })}
              {sorted.length === 0 && <tr><td colSpan={(onRowClick ? 8 : 7) + (showPreviewCol ? 1 : 0)} className="py-8 text-center text-gray-400">No data available</td></tr>}
              {sorted.length > 1 && (() => {
                const gstTotalSpend = totalSpend * 1.18;
                const tRoi = gstTotalSpend > 0 ? totalRev / gstTotalSpend : 0;
                const tAcos = totalRev > 0 ? (gstTotalSpend / totalRev) * 100 : 0;
                return (
                  <tr className="bg-gray-50/80 font-semibold" style={{ borderTop: '1.5px solid #d1d5db' }}>
                    <td className="text-left py-2.5 px-4 text-[#0F0F0F] whitespace-nowrap">Total</td>
                    {showPreviewCol && <td></td>}
                    <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(totalSpend * 1.18, 'currency')}</td>
                    <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{fmtVal(totalRev, 'currency')}</td>
                    <td className="text-right py-2.5 px-3 tabular-nums whitespace-nowrap"><span className={tRoi >= 1 ? 'text-green-600' : 'text-yellow-600'}>{tRoi.toFixed(2)}x</span></td>
                    <td className="text-right py-2.5 px-3 text-gray-800 tabular-nums whitespace-nowrap">{tAcos.toFixed(2)}%</td>
                    <td className="text-right py-2.5 pl-4 pr-3 font-bold text-blue-800 tabular-nums border-l-2 border-blue-200 bg-blue-50/30 whitespace-nowrap">100.00%</td>
                    <td className="text-right py-2.5 pl-4 pr-3 font-bold text-blue-800 tabular-nums bg-blue-50/30 whitespace-nowrap">100.00%</td>
                    {onRowClick && <td></td>}
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const stageTotalSpend = stageRows.reduce((s, r) => s + r.ad_spend, 0);
  const stageTotalRev = stageRows.reduce((s, r) => s + r.total_revenue, 0);
  const chTotalSpend = channelRows.filter(r => !r.isChild).reduce((s, r) => s + r.ad_spend, 0);
  const chTotalRev = channelRows.filter(r => !r.isChild).reduce((s, r) => s + r.total_revenue, 0);
  const ptTotalSpend = platformRows.reduce((s, r) => s + r.ad_spend, 0);
  const ptTotalRev = platformRows.reduce((s, r) => s + r.total_revenue, 0);
  const fmtTotalSpend = formatRows.reduce((s, r) => s + r.ad_spend, 0);
  const fmtTotalRev = formatRows.reduce((s, r) => s + r.total_revenue, 0);

  // pageFilters for Header — only date picker, no channel/brand (we handle those ourselves)
  const pageFilters = { channels: [], channelGated: false };

  // Breadcrumb
  const crumbs = [
    { label: 'Funnel Breakdown', onClick: () => { setDrillStage(null); setDrillChannel(null); setDrillPlatform(null); setDrillFormat(null); setDrillDate(null); } },
    drillStage && { label: drillStage.charAt(0).toUpperCase() + drillStage.slice(1), onClick: () => { setDrillChannel(null); setDrillPlatform(null); setDrillFormat(null); } },
    drillChannel && { label: drillChannel, onClick: () => { setDrillPlatform(null); setDrillFormat(null); } },
    drillPlatform && { label: drillPlatform, onClick: () => { setDrillFormat(null); } },
    drillFormat && { label: drillFormat },
  ].filter(Boolean);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <Sidebar user={user} currentPage="funnel" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header
          user={user}
          filters={headerFilters}
          onFiltersChange={setHeaderFilters}
          pageFilters={pageFilters}
          hideComparison
          monthControl={dateMode === 'monthly' ? (
            <MonthCompare
              availableMonths={availableMonths}
              comparedMonths={comparedMonths}
              currentMonth={currentMonth}
              onAdd={addCompareMonth}
              onRemove={removeCompareMonth}
              onChangeCurrent={setBaseMonth}
            />
          ) : null}
        />

        {/* Funnel-specific filters below header */}
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center gap-3 flex-wrap" onClick={() => { setShowChDD(false); setShowStDD(false); setShowBrDD(false); }}>
          <div onClick={e => e.stopPropagation()}><MultiDD label="Channels" options={CHANNELS} selected={selectedChannels} onChange={setSelectedChannels} show={showChDD} setShow={(v) => { setShowChDD(v); setShowStDD(false); setShowBrDD(false); }} testId="filter-funnel-channel" /></div>
          <div onClick={e => e.stopPropagation()}><MultiDD label="Stages" options={STAGES_OPTIONS} selected={selectedStages} onChange={setSelectedStages} show={showStDD} setShow={(v) => { setShowStDD(v); setShowChDD(false); setShowBrDD(false); }} testId="filter-funnel-stage" /></div>
          <div onClick={e => e.stopPropagation()}><MultiDD label="Brands" options={BRANDS} selected={selectedBrands} onChange={setSelectedBrands} show={showBrDD} setShow={(v) => { setShowBrDD(v); setShowChDD(false); setShowStDD(false); }} testId="filter-funnel-brand" /></div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>
        ) : (
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-7xl mx-auto space-y-5">
            {/* Title + breadcrumb */}
            <div>
              <h1 className="text-2xl font-bold text-[#0F0F0F] tracking-tight mb-1">Funnel Breakdown</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                {crumbs.map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {i > 0 && <ChevronRight className="w-3 h-3" />}
                    {b.onClick ? <button onClick={b.onClick} className="hover:text-[#32B4AC]">{b.label}</button> : <span className="text-[#0F0F0F] font-medium">{b.label}</span>}
                  </div>
                ))}
              </div>
            </div>

            {/* Month comparison chips now render inside the Header (above the
                Daily/Monthly toggle); no in-page strip required. */}

            {/* Hide/Show graphs toggle */}
            <div className="flex justify-end -mb-2">
              <button
                onClick={() => setHideGraphs(v => !v)}
                data-testid="toggle-hide-graphs"
                className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md px-2 py-1 transition-colors"
                title={hideGraphs ? 'Show graphs' : 'Hide graphs'}
              >
                {hideGraphs
                  ? <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg> Show graphs</>
                  : <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg> Hide graphs</>
                }
              </button>
            </div>

            {/* Charts */}
            {!hideGraphs && (isCompareMode ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {graphs.map(g => (
                  <div key={g.key} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">{g.label}</h3>
                    <ResponsiveContainer width="100%" height={170}>
                      <LineChart data={compareData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                        <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={g.format} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v) => [g.format(v), g.label]} />
                        <Line type="monotone" dataKey={g.key} stroke="#37b6ad" strokeWidth={2.5}
                          dot={<MonthCompareDot />} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ))}
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">{tg.label}</h3>
                    <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                      {['acos', 'tacos', 'roas'].map(o => (
                        <button key={o} onClick={() => setHeaderFilters(p => ({ ...p, thirdGraph: o }))}
                          className={`px-2 py-1 text-[10px] font-semibold uppercase rounded transition-all ${thirdGraph === o ? 'bg-white text-[#0F0F0F] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>{o === 'roas' ? 'ROI' : o}</button>
                      ))}
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={170}>
                    <LineChart data={compareData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                      <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={tg.format} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                        formatter={(v) => [tg.format(v), tg.label]} />
                      <Line type="monotone" dataKey={tg.key} stroke="#37b6ad" strokeWidth={2.5}
                        dot={<MonthCompareDot />} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : !hasAnyDateWise ? (
              <div className="bg-white border border-amber-200 rounded-xl p-6 shadow-sm" data-testid="funnel-chart-empty-state">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center shrink-0">
                    <Info className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-[#0F0F0F]">No date-wise data available for this view</h3>
                    <p className="text-xs text-gray-500 mt-1">The properties/campaigns at this level report month-wise or week-wise only. Aggregate totals still appear in the metric cards and the table below.</p>
                    {chartEntries.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {chartEntries.map((e) => (
                          <span key={e.name} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 text-[11px] text-amber-800">
                            {e.name} · {e.type}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : lineData.length > 0 && (
              <>
                {chartCaption && (
                  <p className="text-[11px] text-gray-500 -mt-1 mb-1" data-testid="funnel-chart-caption">{chartCaption}</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {graphs.map(g => (
                    <div key={g.key} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">{g.label}</h3>
                      <ResponsiveContainer width="100%" height={170}>
                        <LineChart data={lineData} onClick={(data) => { if (dateMode === 'daily' && data?.activePayload?.[0]?.payload?.rawDate) setDrillDate(data.activePayload[0].payload.rawDate); }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                          <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={g.format} />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                            formatter={(v) => [g.format(v), g.label]} />
                          <Line type="monotone" dataKey={g.key} stroke={g.color} strokeWidth={2.5}
                            dot={{ r: 3, fill: g.color, stroke: g.color }} activeDot={<ClickableDot fill={g.color} />} />
                        </LineChart>
                      </ResponsiveContainer>
                      {dateMode === 'daily' && <p className="text-[10px] text-gray-400 mt-1 text-center">Click a data point to view day details</p>}
                    </div>
                  ))}
                  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-700">{tg.label}</h3>
                      <div className="flex items-center bg-gray-100 rounded-md p-0.5">
                        {['acos', 'tacos', 'roas'].map(o => (
                          <button key={o} onClick={() => setHeaderFilters(p => ({ ...p, thirdGraph: o }))}
                            className={`px-2 py-1 text-[10px] font-semibold uppercase rounded transition-all ${thirdGraph === o ? 'bg-white text-[#0F0F0F] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>{o === 'roas' ? 'ROI' : o}</button>
                        ))}
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={170}>
                      <LineChart data={lineData} onClick={(data) => { if (dateMode === 'daily' && data?.activePayload?.[0]?.payload?.rawDate) setDrillDate(data.activePayload[0].payload.rawDate); }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '10px' }} />
                        <YAxis stroke="#9ca3af" style={{ fontSize: '10px' }} tickFormatter={tg.format} />
                        <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                          formatter={(v) => [tg.format(v), tg.label]} />
                        <Line type="monotone" dataKey={tg.key} stroke={tg.color} strokeWidth={2.5}
                          dot={{ r: 3, fill: tg.color, stroke: tg.color }} activeDot={<ClickableDot fill={tg.color} />} />
                      </LineChart>
                    </ResponsiveContainer>
                    {dateMode === 'daily' && <p className="text-[10px] text-gray-400 mt-1 text-center">Click a data point to view day details</p>}
                  </div>
                </div>
              </>
            ))}

            {/* Date filter badge */}
            {drillDate && (
              <div className="flex items-center gap-2" data-testid="date-filter-badge">
                <div className="inline-flex items-center gap-2 bg-[#32B4AC]/10 border border-[#32B4AC]/30 rounded-lg px-3 py-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#32B4AC]" />
                  <span className="text-sm font-medium text-[#0F0F0F]">{new Date(drillDate).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  <button onClick={() => setDrillDate(null)} className="ml-1 p-0.5 rounded-full hover:bg-[#32B4AC]/20"><X className="w-3.5 h-3.5 text-[#32B4AC]" /></button>
                </div>
                <span className="text-xs text-gray-400">Showing data for selected date only</span>
              </div>
            )}

            {/* Metric Cards */}
            <MetricCards />

            {/* Back button */}
            {drillStage && (
              <button onClick={() => { if (drillFormat) setDrillFormat(null); else if (drillPlatform) setDrillPlatform(null); else if (drillChannel) setDrillChannel(null); else setDrillStage(null); }}
                className="flex items-center gap-2 text-[#32B4AC] hover:text-[#2a9d95] text-sm"><ArrowLeft className="w-4 h-4" /> Back</button>
            )}

            {/* Unmapped campaigns banner */}
            {(() => {
              const unmappedRows = filtered.filter(r => r.funnel_stage === 'unmapped');
              const unmappedCampaigns = new Set(unmappedRows.map(r => r.campaign_name).filter(Boolean));
              const unmappedSpend = unmappedRows.reduce((s, r) => s + (r.ad_spend || 0), 0);
              if (unmappedCampaigns.size === 0) return null;
              return (
                <div className="bg-amber-50/70 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="funnel-split-unmapped-banner">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">{unmappedCampaigns.size} campaign{unmappedCampaigns.size === 1 ? '' : 's'} unmapped</p>
                    <p className="text-xs text-amber-800/90 mt-0.5">Rs. {unmappedSpend.toLocaleString('en-IN', { maximumFractionDigits: 0 })} of spend isn't classified into Awareness or Conversion. Map these campaigns to include them in funnel splits.</p>
                  </div>
                  <button
                    type="button"
                    data-testid="funnel-split-open-mapping-btn"
                    onClick={() => {
                      // Persist the unmapped campaign list so Funnel Mapping can pre-filter on landing.
                      // Using sessionStorage avoids stuffing 100s of names into the URL.
                      try {
                        sessionStorage.setItem(
                          'funnel-mapping:focus-unmapped',
                          JSON.stringify({ campaigns: Array.from(unmappedCampaigns), at: Date.now() })
                        );
                      } catch (e) { /* ignore quota errors */ }
                      navigate('/funnel-mapping?focus=unmapped');
                    }}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 transition-colors whitespace-nowrap"
                  >
                    Open Funnel Mapping <span aria-hidden>→</span>
                  </button>
                </div>
              );
            })()}

            {/* Single cascading table — only one visible at a time */}
            {!drillStage && (
              <RenderTable title="Funnel Stage" rows={stageRows} totalSpend={stageTotalSpend} totalRev={stageTotalRev}
                onRowClick={(key) => { setDrillStage(key); setDrillChannel(null); setDrillPlatform(null); }} level="stage" classByKey={chartClassByKey} />
            )}

            {drillStage && !drillChannel && (
              <RenderTable title={`Channel (${drillStage.charAt(0).toUpperCase() + drillStage.slice(1)})`} rows={channelRows} totalSpend={chTotalSpend} totalRev={chTotalRev}
                onRowClick={(key, row) => {
                  if (row?.isChild && row.parentKey === 'D2C') {
                    // Clicking a D2C sub-row (Meta Ads / Google Ads / WhatsApp)
                    // drills straight into that platform-type inside D2C.
                    setDrillChannel('D2C');
                    setDrillPlatform(row.name);
                  } else {
                    setDrillChannel(key);
                    setDrillPlatform(null);
                  }
                }} level="channel" classByKey={chartClassByKey} />
            )}

            {drillStage && drillChannel && !drillPlatform && (
              <RenderTable title={`Platform Type (${drillChannel})`} rows={platformRows} totalSpend={ptTotalSpend} totalRev={ptTotalRev}
                onRowClick={(key) => setDrillPlatform(key)} level="platform" classByKey={chartClassByKey} />
            )}

            {drillStage && drillChannel && drillPlatform && !drillFormat && (
              <RenderTable title={`Ad Format (${drillPlatform})`} rows={formatRows} totalSpend={fmtTotalSpend} totalRev={fmtTotalRev}
                onRowClick={(key) => { setDrillFormat(key); setCampaignSort({ key: 'ad_spend', dir: 'desc' }); }} level="format" channelForPreview={drillChannel} classByKey={chartClassByKey} />
            )}

            {/* Level 5: Campaign drill-down */}
            {drillStage && drillChannel && drillPlatform && drillFormat && (() => {
              const campaignRows = rowsContributingTo(filtered, drillStage).filter(r =>
                r.channel === drillChannel &&
                getPlatformType(r) === drillPlatform && r.ad_product === drillFormat
              );
              // Aggregate by campaign_name with per-row stage weighting
              const byCampaign = {};
              for (const r of campaignRows) {
                const cn = r.campaign_name || 'Unknown';
                if (!byCampaign[cn]) byCampaign[cn] = [];
                byCampaign[cn].push(r);
              }
              const aggregatedCampaigns = Object.entries(byCampaign).map(([name, rows]) => {
                let spend = 0, revenue = 0, views = 0, clicks = 0, units = 0;
                for (const r of rows) {
                  const { sw, rw } = stageWeights(r, drillStage);
                  spend += (r.ad_spend || 0) * sw;
                  views += (r.views || 0) * sw;
                  clicks += (r.clicks || 0) * sw;
                  revenue += (r.total_revenue || 0) * rw;
                  units += (r.total_converted_units || 0) * rw;
                }
                const gstSpend = spend * 1.18;
                return {
                  campaign_name: name, ad_spend: spend, total_revenue: revenue, views, clicks,
                  total_converted_units: units,
                  ctr: views > 0 ? (clicks / views) * 100 : 0,
                  cvr: clicks > 0 ? (units / clicks) * 100 : 0,
                  roi: gstSpend > 0 ? revenue / gstSpend : 0,
                  acos: revenue > 0 ? (gstSpend / revenue) * 100 : 0,
                };
              });
              const sortedCampaigns = [...aggregatedCampaigns].sort((a, b) => {
                const av = a[campaignSort.key] ?? 0, bv = b[campaignSort.key] ?? 0;
                if (typeof av === 'string') return campaignSort.dir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
                return campaignSort.dir === 'desc' ? bv - av : av - bv;
              });
              const cols = [
                { key: 'campaign_name', label: 'Campaign Name', align: 'left' },
                { key: 'ad_spend', label: 'Ad Spend', fmt: 'currency' },
                { key: 'views', label: 'Views', fmt: 'number' },
                { key: 'clicks', label: 'Clicks', fmt: 'number' },
                { key: 'ctr', label: 'CTR', fmt: 'pct' },
                { key: 'total_converted_units', label: 'Conv. Units', fmt: 'number' },
                { key: 'cvr', label: 'CVR', fmt: 'pct' },
                { key: 'total_revenue', label: 'Revenue', fmt: 'currency' },
                { key: 'roi', label: 'ROI', fmt: 'decimal' },
                { key: 'acos', label: 'ACOS', fmt: 'pct' },
              ];
              const toggleCSort = (key) => setCampaignSort(prev => prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' });
              const CSortIcon = ({ colKey }) => {
                if (campaignSort.key !== colKey) return <ArrowUpDown className="w-3 h-3 text-gray-300 ml-0.5 inline" />;
                return campaignSort.dir === 'desc' ? <ArrowDown className="w-3 h-3 text-[#32B4AC] ml-0.5 inline" /> : <ArrowUp className="w-3 h-3 text-[#32B4AC] ml-0.5 inline" />;
              };

              // Width per column type — locked to prevent shifts on data refresh.
              const colWidth = (col) => {
                if (col.key === 'campaign_name') return 240;
                if (col.align === 'left') return 160;
                if (col.fmt === 'currency') return 140;
                if (col.fmt === 'pct') return 90;
                if (col.fmt === 'decimal') return 90;
                return 110; // number / default
              };

              return (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-[#0F0F0F]">{drillFormat} — Campaigns</h3>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{sortedCampaigns.length} campaigns</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="text-sm" style={{ tableLayout: 'fixed', width: cols.reduce((s, c) => s + colWidth(c), 0) }} data-testid="funnel-campaign-table">
                      <colgroup>
                        {cols.map(col => <col key={col.key} style={{ width: `${colWidth(col)}px` }} />)}
                      </colgroup>
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50/80">
                          {cols.map(col => (
                            <th key={col.key}
                              className={`${col.align === 'left' ? 'text-left' : 'text-right'} py-2.5 px-3 font-semibold text-gray-500 text-[11px] uppercase tracking-wider whitespace-nowrap align-middle cursor-pointer hover:text-gray-700 select-none`}
                              onClick={() => toggleCSort(col.key)}>
                              <span className={`inline-flex items-center gap-1.5 ${col.align === 'left' ? 'justify-start' : 'justify-end'}`}>
                                <MetricTooltip label={col.key} /><span>{col.label}</span><CSortIcon colKey={col.key} />
                              </span>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCampaigns.map((row, idx) => (
                          <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50/40">
                            {cols.map(col => (
                              <td key={col.key}
                                className={`${col.align === 'left' ? 'text-left' : 'text-right'} py-2.5 px-3 text-gray-700 tabular-nums whitespace-nowrap ${col.key === 'campaign_name' ? 'font-medium text-[#0F0F0F] truncate' : ''}`}
                                title={col.key === 'campaign_name' ? row[col.key] : undefined}>
                                {col.align === 'left' ? (
                                  (() => {
                                    // Show a small icon next to left-aligned identity cells (brand /
                                    // campaign name / ad group). Brand column uses brand icon; the
                                    // campaign_name column tries the row.brand icon for a hint.
                                    const val = row[col.key] || '-';
                                    let url = '';
                                    if (col.key === 'brand') url = brandIcon(val);
                                    else if (col.key === 'campaign_name' && row.brand) url = brandIcon(row.brand);
                                    return (
                                      <span className="inline-flex items-center gap-1.5">
                                        {url && <img src={url} alt="" className="w-4 h-4 rounded object-contain shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                                        <span>{val}</span>
                                      </span>
                                    );
                                  })()
                                ) :
                                  col.fmt === 'currency' ? fmtVal(row[col.key], 'currency') :
                                  col.fmt === 'number' ? fmtVal(row[col.key], 'number') :
                                  col.fmt === 'pct' ? `${(row[col.key] || 0).toFixed(2)}%` :
                                  col.fmt === 'decimal' ? `${(row[col.key] || 0).toFixed(2)}x` :
                                  row[col.key]}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {sortedCampaigns.length === 0 && (
                          <tr><td colSpan={cols.length} className="py-8 text-center text-gray-400">No campaigns found for this path</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </main>
        )}

        {/* Metric Picker Modal */}
        {showMetricPicker && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => { setShowMetricPicker(false); setShowCustomForm(false); }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
                <h2 className="text-base font-bold text-[#0F0F0F]">Add / Remove Metrics</h2>
                <button onClick={() => { setShowMetricPicker(false); setShowCustomForm(false); }} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-500" /></button>
              </div>
              <div className="p-4 space-y-1 overflow-y-auto flex-1">
                {[...ALL_METRICS, ...customMetrics].map(m => {
                  const active = visibleMetrics.includes(m.key);
                  return (
                    <button key={m.key} onClick={() => setVisibleMetrics(prev => active ? prev.filter(k => k !== m.key) : [...prev, m.key])}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${active ? 'bg-[#32B4AC]/10 border border-[#32B4AC]/30' : 'bg-gray-50 border border-gray-200 hover:bg-gray-100'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${active ? 'text-[#0F0F0F]' : 'text-gray-500'}`}>{m.label}</span>
                        {m.formula && <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">custom</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {m.formula && <button onClick={(e) => { e.stopPropagation(); setCustomMetrics(prev => prev.filter(c => c.key !== m.key)); setVisibleMetrics(prev => prev.filter(k => k !== m.key)); }} className="text-red-400 hover:text-red-600"><Minus className="w-3.5 h-3.5" /></button>}
                        {active ? <Minus className="w-4 h-4 text-red-400" /> : <Plus className="w-4 h-4 text-[#32B4AC]" />}
                      </div>
                    </button>
                  );
                })}
                {!showCustomForm ? (
                  <button onClick={() => setShowCustomForm(true)} className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300 text-sm mt-2">
                    <Plus className="w-4 h-4" /> Create Custom Metric
                  </button>
                ) : (
                  <div className="mt-2 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                    <div className="text-sm font-semibold text-[#0F0F0F]">New Custom Metric</div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Metric Name</label>
                      <input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="e.g. Cost Per Unit" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#32B4AC]" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Formula / Function</label>
                      <input value={customFormula} onChange={(e) => setCustomFormula(e.target.value)} placeholder="e.g. spend / units" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#32B4AC]" />
                      <p className="text-[10px] text-gray-400 mt-1">Variables: spend, revenue, views, clicks, units, ctr, cvr, roi, acos</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setShowCustomForm(false)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
                      <button onClick={() => {
                        if (!customName.trim() || !customFormula.trim()) return;
                        const key = `custom_${Date.now()}`;
                        setCustomMetrics(prev => [...prev, { key, label: customName.trim(), formula: customFormula.trim(), fmt: 'number' }]);
                        setVisibleMetrics(prev => [...prev, key]);
                        setCustomName(''); setCustomFormula(''); setShowCustomForm(false);
                      }} className="px-3 py-1.5 text-sm bg-[#32B4AC] text-white rounded-lg hover:bg-[#2a9d95]">
                        Add Metric
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="px-6 py-3 border-t border-gray-200 flex justify-end shrink-0">
                <button onClick={() => { setShowMetricPicker(false); setShowCustomForm(false); }} className="px-4 py-2 bg-[#32B4AC] text-white text-sm rounded-lg hover:bg-[#2a9d95]">Done</button>
              </div>
            </div>
          </div>
        )}

        {/* Ad placement preview modal — mirrors the modal on Ad Format Breakdown */}
        {previewProduct && AD_PREVIEWS[previewProduct] && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setPreviewProduct(null)} data-testid="preview-modal-overlay">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-bold text-[#0F0F0F]">{AD_PREVIEWS[previewProduct].label}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Ad placement previews on {drillChannel || 'this channel'}</p>
                </div>
                <button onClick={() => setPreviewProduct(null)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="preview-modal-close">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6 space-y-6">
                {AD_PREVIEWS[previewProduct].placements.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">No placement previews available yet for this ad product.</p>
                ) : AD_PREVIEWS[previewProduct].placements.map((placement, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#32B4AC]/10 text-[#32B4AC] text-xs font-bold">{idx + 1}</span>
                      <h3 className="text-sm font-semibold text-[#0F0F0F]">{placement.name}</h3>
                    </div>
                    <p className="text-xs text-gray-500 ml-8">{placement.description}</p>
                    <div className="ml-8 rounded-xl border border-gray-200 overflow-hidden bg-gray-50 shadow-sm">
                      <img src={placement.image} alt={placement.name} className="w-full h-auto object-contain max-h-[400px]" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
